import { getAppointmentTimingStatus, type AppointmentTimingStatus } from '@/lib/appointmentCardPresentation';
import type { WorkItem, WorkflowStateRecord } from '../types';
import {
  formatSmartTaskStatusLabel,
  getSmartTaskVisibleStatus,
  isSmartTaskTerminalStatus,
} from './templateActionNormalization';
import { getWorkItemTypeRegistry } from '@/config/work-items/configLoader';

interface WorkflowContextLike {
  current_state: string | null;
  active_work_item_id: string | null;
  traveling_to_work_item_id: string | null;
}

export interface WorkItemStatusContext {
  workflowState?: WorkflowContextLike | null;
  currentState?: string | null;
  activeWorkItemId?: string | null;
  travelingToWorkItemId?: string | null;
  detourPausedIds?: Set<string>;
}

export interface WorkItemPanelStatusInfo {
  key: string;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  dotColor: string;
  isTerminal: boolean;
  isOverSchedule: boolean;
  isOperationallyActive: boolean;
}

export interface SmartTaskCurrentStatusTargetLike {
  item_type?: string | null;
  item_subtype?: string | null;
  item_number?: string | null;
  display_number?: string | null;
  title?: string | null;
}

export interface SmartTaskCurrentStatusInfo {
  key: string;
  label: string;
  color: string;
  isAttention: boolean;
}

export interface RouteDisplayStatusLike {
  id: string;
  status?: string | null;
  appointmentId?: string;
  parentJobId?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  started_at?: string | null;
}

export type RouteDisplayStopState = 'completed' | 'active' | 'over_schedule' | 'traveling' | 'upcoming';

export interface WorkItemAppointmentTimingInfo {
  timingStatus: AppointmentTimingStatus | null;
  isRunningLate: boolean;
  lateMinutes: number;
  isMissed: boolean;
  isBehindSchedule: boolean;
  behindScheduleMinutes: number;
  isAllocatedTimeExceeded: boolean;
  allocatedTimeExceededMinutes: number;
}

export interface WorkItemAppointmentScheduleVarianceInfo {
  isBehindSchedule: boolean;
  behindScheduleMinutes: number;
  isAllocatedTimeExceeded: boolean;
  allocatedTimeExceededMinutes: number;
}

const FALLBACK_ACTIVE_WORK_STATUS_KEYS = new Set(['checked_in', 'working', 'in_progress', 'completing']);
const FALLBACK_ACTIVE_DISPLAY_STATUS_KEYS = new Set(['arrived', 'current', 'on_site', ...FALLBACK_ACTIVE_WORK_STATUS_KEYS]);
const FALLBACK_TRAVEL_STATUS_KEYS = new Set(['traveling', 'travel_paused', 'en_route', 'on_route']);
const FALLBACK_UPCOMING_ROUTE_SNAPSHOT_STATUS_KEYS = new Set(['pending', 'scheduled', 'assigned', 'confirmed', 'dispatched']);

let _activeWorkStatusKeys: Set<string> | null = null;
let _activeDisplayStatusKeys: Set<string> | null = null;
let _travelStatusKeys: Set<string> | null = null;
let _upcomingRouteSnapshotStatusKeys: Set<string> | null = null;

function getActiveWorkStatusKeys(): Set<string> {
  if (_activeWorkStatusKeys) return _activeWorkStatusKeys;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByWorkflowPhase('work');
      if (ssotStatuses.length > 0) {
        _activeWorkStatusKeys = new Set(ssotStatuses);
        return _activeWorkStatusKeys;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_ACTIVE_WORK_STATUS_KEYS;
}

function getActiveDisplayStatusKeys(): Set<string> {
  if (_activeDisplayStatusKeys) return _activeDisplayStatusKeys;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const workStatuses = registry.getOperationalStatusesByWorkflowPhase('work');
      const siteStatuses = registry.getOperationalStatusesByWorkflowPhase('site');
      if (workStatuses.length > 0 || siteStatuses.length > 0) {
        _activeDisplayStatusKeys = new Set([...workStatuses, ...siteStatuses, 'current']);
        return _activeDisplayStatusKeys;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_ACTIVE_DISPLAY_STATUS_KEYS;
}

function getTravelStatusKeys(): Set<string> {
  if (_travelStatusKeys) return _travelStatusKeys;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByWorkflowPhase('travel');
      if (ssotStatuses.length > 0) {
        _travelStatusKeys = new Set(ssotStatuses);
        return _travelStatusKeys;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_TRAVEL_STATUS_KEYS;
}

function getUpcomingRouteSnapshotStatusKeys(): Set<string> {
  if (_upcomingRouteSnapshotStatusKeys) return _upcomingRouteSnapshotStatusKeys;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByWorkflowPhase('upcoming');
      if (ssotStatuses.length > 0) {
        _upcomingRouteSnapshotStatusKeys = new Set(ssotStatuses);
        return _upcomingRouteSnapshotStatusKeys;
      }
    }
  } catch { /* fallback below */ }
  return FALLBACK_UPCOMING_ROUTE_SNAPSHOT_STATUS_KEYS;
}

let _closedStatusSet: Set<string> | null = null;

function getClosedStatusSetFromSSOT(): Set<string> {
  if (_closedStatusSet) return _closedStatusSet;
  try {
    const registry = getWorkItemTypeRegistry();
    if (registry.isInitialized()) {
      const ssotStatuses = registry.getOperationalStatusesByCategory('closed');
      if (ssotStatuses.length > 0) {
        _closedStatusSet = new Set(ssotStatuses);
        return _closedStatusSet;
      }
    }
  } catch { /* fallback below */ }
  return new Set(['completed', 'resolved', 'closed', 'ai_review']);
}
const SMART_TASK_SHORT_TYPE_LABELS: Record<string, string> = {
  appointment: 'APT',
  audit: 'AUD',
  collection: 'COL',
  inspection: 'INS',
  job: 'JOB',
  route: 'Route',
  route_stop: 'Route',
  task: 'TAS',
  ticket: 'TKT',
};

function resolveContext(context: WorkItemStatusContext = {}): WorkflowContextLike {
  return {
    current_state: context.currentState ?? context.workflowState?.current_state ?? null,
    active_work_item_id: context.activeWorkItemId ?? context.workflowState?.active_work_item_id ?? null,
    traveling_to_work_item_id: context.travelingToWorkItemId ?? context.workflowState?.traveling_to_work_item_id ?? null,
  };
}

function parseTimestamp(value?: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : null;
}

function toNonEmptyString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function getSmartTaskShortTypeLabel(
  itemType?: string | null,
  itemSubtype?: string | null,
): string | null {
  const normalizedSubtype = normalizeStatusKey(itemSubtype);
  if (normalizedSubtype && SMART_TASK_SHORT_TYPE_LABELS[normalizedSubtype]) {
    return SMART_TASK_SHORT_TYPE_LABELS[normalizedSubtype];
  }

  const normalizedType = normalizeStatusKey(itemType);
  if (normalizedType && SMART_TASK_SHORT_TYPE_LABELS[normalizedType]) {
    return SMART_TASK_SHORT_TYPE_LABELS[normalizedType];
  }

  return null;
}

function buildSmartTaskCurrentStatusTargetLabel(
  target?: SmartTaskCurrentStatusTargetLike | null,
): string | null {
  if (!target) {
    return null;
  }

  const shortTypeLabel = getSmartTaskShortTypeLabel(target.item_type, target.item_subtype);
  const rawDisplayNumber = toNonEmptyString(target.display_number) || toNonEmptyString(target.item_number);
  const displayNumber = rawDisplayNumber
    ? rawDisplayNumber.split('|')[0]?.trim() || rawDisplayNumber
    : null;

  if (displayNumber) {
    if (!shortTypeLabel || shortTypeLabel === 'Route') {
      return displayNumber;
    }

    const upperDisplayNumber = displayNumber.toUpperCase();
    const upperShortTypeLabel = shortTypeLabel.toUpperCase();
    const alreadyPrefixed = upperDisplayNumber === upperShortTypeLabel
      || upperDisplayNumber.startsWith(upperShortTypeLabel)
      || upperDisplayNumber.startsWith(`${upperShortTypeLabel}-`)
      || upperDisplayNumber.startsWith(`${upperShortTypeLabel} `);

    return alreadyPrefixed ? displayNumber : `${shortTypeLabel} ${displayNumber}`;
  }

  return toNonEmptyString(target.title) || shortTypeLabel;
}

export function getSmartTaskCurrentStatusInfo(options: {
  currentState?: string | null;
  activeTarget?: SmartTaskCurrentStatusTargetLike | null;
  travelTarget?: SmartTaskCurrentStatusTargetLike | null;
}): SmartTaskCurrentStatusInfo {
  const normalizedState = normalizeStatusKey(options.currentState);
  const activeTargetLabel = buildSmartTaskCurrentStatusTargetLabel(options.activeTarget);
  const travelTargetLabel = buildSmartTaskCurrentStatusTargetLabel(options.travelTarget);

  switch (normalizedState) {
    case 'traveling':
    case 'on_route':
    case 'en_route':
      return {
        key: normalizedState || 'traveling',
        label: travelTargetLabel ? `Travel to ${travelTargetLabel}` : 'Traveling',
        color: '#2563eb',
        isAttention: false,
      };
    case 'travel_paused':
      return {
        key: normalizedState,
        label: activeTargetLabel ? `Paused ${activeTargetLabel}` : 'Paused',
        color: '#f59e0b',
        isAttention: false,
      };
    case 'arrived':
    case 'on_site':
    case 'current':
    case 'checked_in':
    case 'working':
    case 'in_progress':
    case 'completing':
      return {
        key: normalizedState || 'working',
        label: activeTargetLabel ? `Working ${activeTargetLabel}` : 'Working',
        color: '#22c55e',
        isAttention: false,
      };
    case 'paused':
      return {
        key: normalizedState,
        label: activeTargetLabel ? `Paused ${activeTargetLabel}` : 'Paused',
        color: '#f59e0b',
        isAttention: false,
      };
    case 'vehicle_required':
      return {
        key: normalizedState,
        label: 'Vehicle Required',
        color: '#ef4444',
        isAttention: true,
      };
    case 'inspection_pending':
      return {
        key: normalizedState,
        label: 'Inspection Required',
        color: '#f59e0b',
        isAttention: true,
      };
    case 'resolved':
    case 'completed':
    case 'cancelled':
    case 'scheduled':
    case 'ready':
    case 'idle':
    case '':
      return {
        key: normalizedState || 'idle',
        label: 'Smart Task - Idle',
        color: '#94a3b8',
        isAttention: false,
      };
    default:
      return {
        key: normalizedState || 'idle',
        label: formatSmartTaskStatusLabel(normalizedState, options.activeTarget?.item_type) || 'Smart Task - Idle',
        color: '#94a3b8',
        isAttention: false,
      };
  }
}

function getCustomFieldRecord(item: Pick<WorkItem, 'custom_fields'>): Record<string, unknown> {
  const customFields = item.custom_fields;
  return customFields && typeof customFields === 'object' && !Array.isArray(customFields)
    ? customFields as Record<string, unknown>
    : {};
}

function getCheckInStartedAtMs(
  item: Pick<WorkItem, 'started_at' | 'custom_fields'>,
): number | null {
  const customFields = getCustomFieldRecord(item);
  const checkInTimestamp = customFields.check_in_timestamp;
  return parseTimestamp(typeof checkInTimestamp === 'string' ? checkInTimestamp : item.started_at);
}

function getScheduledWindowMs(
  item: Pick<WorkItem, 'scheduled_start' | 'scheduled_end'>,
): { startMs: number | null; endMs: number | null; durationMs: number | null } {
  const startMs = parseTimestamp(item.scheduled_start);
  const endMs = parseTimestamp(item.scheduled_end);
  const durationMs = startMs != null && endMs != null && endMs > startMs
    ? endMs - startMs
    : null;

  return { startMs, endMs, durationMs };
}

export function getWorkItemAppointmentScheduleVarianceInfo(
  item: Pick<WorkItem, 'id' | 'item_type' | 'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'custom_fields'>,
  context: WorkItemStatusContext = {},
): WorkItemAppointmentScheduleVarianceInfo {
  if (normalizeStatusKey(item.item_type) !== 'appointment') {
    return {
      isBehindSchedule: false,
      behindScheduleMinutes: 0,
      isAllocatedTimeExceeded: false,
      allocatedTimeExceededMinutes: 0,
    };
  }

  const exactStatus = getWorkItemExactWorkflowStatus(item, context);
  const isActivelyWorked = isActivelyWorkedStatusKey(exactStatus);
  const { endMs, durationMs } = getScheduledWindowMs(item);
  const checkInStartedAtMs = getCheckInStartedAtMs(item);
  const nowMs = Date.now();

  const workedDurationMs = checkInStartedAtMs != null
    ? Math.max(0, nowMs - checkInStartedAtMs)
    : 0;

  const isAllocatedTimeExceeded = Boolean(
    isActivelyWorked
    && durationMs != null
    && workedDurationMs > durationMs
  );

  const allocatedTimeExceededMinutes = isAllocatedTimeExceeded && durationMs != null
    ? Math.max(0, Math.round((workedDurationMs - durationMs) / 60000))
    : 0;

  const isPastScheduledEnd = endMs != null && nowMs > endMs;
  const isBehindSchedule = Boolean(
    !isAllocatedTimeExceeded
    && isActivelyWorked
    && isPastScheduledEnd
  );

  const behindScheduleMinutes = isBehindSchedule && endMs != null
    ? Math.max(0, Math.round((nowMs - endMs) / 60000))
    : 0;

  return {
    isBehindSchedule,
    behindScheduleMinutes,
    isAllocatedTimeExceeded,
    allocatedTimeExceededMinutes,
  };
}

function mapWorkflowStatusForTiming(status: string): string {
  switch (status) {
    case 'traveling':
    case 'travel_paused':
      return 'en_route';
    case 'paused':
      return 'in_progress';
    case 'current':
      return 'on_site';
    default:
      return status;
  }
}

function buildTimingStatusInput(
  item: Pick<WorkItem, 'item_type' | 'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'custom_fields'>,
  statusForTiming: string,
) {
  const customFields = { ...getCustomFieldRecord(item) };
  const normalizedStatus = normalizeStatusKey(statusForTiming);
  const isTravelTimingStatus = ['traveling', 'travel_paused', 'en_route', 'on_route'].includes(normalizedStatus);
  const isOnSiteTimingStatus = isTravelTimingStatus || ['arrived', 'on_site', 'current', 'checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedStatus);
  const isWorkingTimingStatus = ['checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedStatus);

  if (!isTravelTimingStatus) {
    delete customFields.travel_started_at;
  }

  if (!isOnSiteTimingStatus) {
    delete customFields.arrived_at;
  }

  if (isWorkingTimingStatus) {
    if (!customFields.check_in_timestamp && item.started_at) {
      customFields.check_in_timestamp = item.started_at;
    }
  } else {
    delete customFields.check_in_timestamp;
  }

  return {
    item_type: item.item_type,
    status: statusForTiming,
    scheduled_start: item.scheduled_start,
    scheduled_end: item.scheduled_end,
    custom_fields: customFields,
  };
}

export function getWorkItemAppointmentTimingInfo(
  item: Pick<WorkItem, 'id' | 'item_type' | 'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'custom_fields'>,
  context: WorkItemStatusContext = {},
): WorkItemAppointmentTimingInfo {
  if (normalizeStatusKey(item.item_type) !== 'appointment') {
    return {
      timingStatus: null,
      isRunningLate: false,
      lateMinutes: 0,
      isMissed: false,
      isBehindSchedule: false,
      behindScheduleMinutes: 0,
      isAllocatedTimeExceeded: false,
      allocatedTimeExceededMinutes: 0,
    };
  }

  const exactStatus = getWorkItemExactWorkflowStatus(item, context);
  const timingStatus = getAppointmentTimingStatus(
    buildTimingStatusInput(item, mapWorkflowStatusForTiming(exactStatus)),
  );
  const isRunningLate = ['late', 'onroute_late', 'missed'].includes(timingStatus);
  const lateMinutes = isRunningLate && timingStatus !== 'missed' && item.scheduled_start
    ? Math.max(0, Math.round((Date.now() - new Date(item.scheduled_start).getTime()) / 60000))
    : 0;
  const varianceInfo = getWorkItemAppointmentScheduleVarianceInfo(item, context);

  return {
    timingStatus,
    isRunningLate,
    lateMinutes,
    isMissed: timingStatus === 'missed',
    isBehindSchedule: varianceInfo.isBehindSchedule,
    behindScheduleMinutes: varianceInfo.behindScheduleMinutes,
    isAllocatedTimeExceeded: varianceInfo.isAllocatedTimeExceeded,
    allocatedTimeExceededMinutes: varianceInfo.allocatedTimeExceededMinutes,
  };
}

export function normalizeStatusKey(value?: string | null): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

export function getWorkItemExactWorkflowStatus(
  item: Pick<WorkItem, 'id' | 'status' | 'custom_fields'>,
  context: WorkItemStatusContext = {},
): string {
  const workflowContext = resolveContext(context);
  const normalizedState = normalizeStatusKey(workflowContext.current_state);
  const itemId = String(item.id);
  const activeWorkItemId = workflowContext.active_work_item_id != null
    ? String(workflowContext.active_work_item_id)
    : null;
  const travelingToWorkItemId = workflowContext.traveling_to_work_item_id != null
    ? String(workflowContext.traveling_to_work_item_id)
    : null;

  if (context.detourPausedIds?.has(itemId)) {
    return 'paused';
  }

  if (travelingToWorkItemId === itemId && ['traveling', 'travel_paused'].includes(normalizedState)) {
    return normalizedState;
  }

  if (activeWorkItemId === itemId && ['arrived', 'checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedState)) {
    return normalizedState;
  }

  return normalizeStatusKey(getSmartTaskVisibleStatus(item.status, item as Record<string, unknown>));
}

export function isTravelLikeWorkItemStatus(statusKey?: string | null): boolean {
  return getTravelStatusKeys().has(normalizeStatusKey(statusKey));
}

export function isActivelyWorkedStatusKey(statusKey?: string | null): boolean {
  return getActiveWorkStatusKeys().has(normalizeStatusKey(statusKey));
}

export function isAtSiteStatusKey(statusKey?: string | null): boolean {
  return ['arrived', 'current', 'on_site'].includes(normalizeStatusKey(statusKey));
}

export function isOperationallyActiveStatusKey(statusKey?: string | null): boolean {
  const normalized = normalizeStatusKey(statusKey);
  return getTravelStatusKeys().has(normalized) || getActiveDisplayStatusKeys().has(normalized) || normalized === 'paused';
}

export function isWorkItemOverSchedule(
  item: Pick<WorkItem, 'id' | 'item_type' | 'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'custom_fields'>,
  context: WorkItemStatusContext = {},
): boolean {
  if (normalizeStatusKey(item.item_type) === 'appointment') {
    return getWorkItemAppointmentScheduleVarianceInfo(item, context).isAllocatedTimeExceeded;
  }

  const exactStatus = getWorkItemExactWorkflowStatus(item, context);
  if (!isActivelyWorkedStatusKey(exactStatus)) {
    return false;
  }

  const startMs = parseTimestamp(item.scheduled_start);
  const endMs = parseTimestamp(item.scheduled_end);
  if (startMs == null || endMs == null || endMs <= startMs) {
    return false;
  }

  const allocatedDurationMs = endMs - startMs;
  const startedAtMs = getCheckInStartedAtMs(item);
  if (startedAtMs != null) {
    return Date.now() - startedAtMs > allocatedDurationMs;
  }

  return Date.now() > endMs;
}

export function getWorkItemPanelStatusInfo(
  item: Pick<WorkItem, 'id' | 'item_type' | 'status' | 'scheduled_start' | 'scheduled_end' | 'started_at' | 'custom_fields'>,
  context: WorkItemStatusContext = {},
): WorkItemPanelStatusInfo {
  const exactStatus = getWorkItemExactWorkflowStatus(item, context);
  const normalizedStatus = normalizeStatusKey(exactStatus);
  const isOverSchedule = isWorkItemOverSchedule(item, context);

  if (getClosedStatusSetFromSSOT().has(normalizedStatus)) {
    return {
      key: 'closed',
      label: 'Closed',
      color: 'text-gray-500 dark:text-gray-400',
      bgColor: 'bg-gray-50 dark:bg-gray-800',
      borderColor: 'border-gray-300 dark:border-gray-600',
      dotColor: 'bg-gray-400',
      isTerminal: true,
      isOverSchedule: false,
      isOperationallyActive: false,
    };
  }

  if (normalizedStatus === 'unresolved') {
    return {
      key: normalizedStatus,
      label: 'Unresolved',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-400',
      dotColor: 'bg-amber-500',
      isTerminal: true,
      isOverSchedule: false,
      isOperationallyActive: false,
    };
  }

  if (normalizedStatus === 'rescheduled') {
    return {
      key: normalizedStatus,
      label: 'Rescheduled',
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20',
      borderColor: 'border-orange-400',
      dotColor: 'bg-orange-500',
      isTerminal: true,
      isOverSchedule: false,
      isOperationallyActive: false,
    };
  }

  if (isOverSchedule) {
    return {
      key: 'over_schedule',
      label: 'Over Schedule',
      color: 'text-orange-800 dark:text-orange-300',
      bgColor: 'bg-orange-50 dark:bg-orange-900/25',
      borderColor: 'border-orange-400',
      dotColor: 'bg-orange-500',
      isTerminal: false,
      isOverSchedule: true,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'travel_paused') {
    return {
      key: normalizedStatus,
      label: 'Travel Paused',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-400',
      dotColor: 'bg-amber-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (getTravelStatusKeys().has(normalizedStatus)) {
    return {
      key: normalizedStatus,
      label: normalizedStatus === 'traveling' ? 'Traveling' : formatSmartTaskStatusLabel(normalizedStatus, item.item_type),
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-400',
      dotColor: 'bg-blue-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (getActiveDisplayStatusKeys().has(normalizedStatus) && normalizedStatus !== 'paused') {
    return {
      key: normalizedStatus,
      label: 'At Site',
      color: 'text-cyan-600 dark:text-cyan-400',
      bgColor: 'bg-cyan-50 dark:bg-cyan-900/20',
      borderColor: 'border-cyan-400',
      dotColor: 'bg-cyan-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'paused') {
    return {
      key: normalizedStatus,
      label: 'Paused',
      color: 'text-amber-600 dark:text-amber-400',
      bgColor: 'bg-amber-50 dark:bg-amber-900/20',
      borderColor: 'border-amber-400',
      dotColor: 'bg-amber-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'completing') {
    return {
      key: normalizedStatus,
      label: 'Completing',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-400',
      dotColor: 'bg-green-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'checked_in' || normalizedStatus === 'working') {
    return {
      key: normalizedStatus,
      label: 'In Progress',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-400',
      dotColor: 'bg-green-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'in_progress') {
    return {
      key: normalizedStatus,
      label: 'In Progress',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-50 dark:bg-green-900/20',
      borderColor: 'border-green-400',
      dotColor: 'bg-green-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: true,
    };
  }

  if (normalizedStatus === 'dispatched') {
    return {
      key: normalizedStatus,
      label: 'Dispatched',
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20',
      borderColor: 'border-purple-400',
      dotColor: 'bg-purple-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: false,
    };
  }

  if (getUpcomingRouteSnapshotStatusKeys().has(normalizedStatus)) {
    return {
      key: normalizedStatus || 'scheduled',
      label: 'Scheduled',
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20',
      borderColor: 'border-blue-400',
      dotColor: 'bg-blue-500',
      isTerminal: false,
      isOverSchedule: false,
      isOperationallyActive: false,
    };
  }

  const isTerminal = isSmartTaskTerminalStatus(normalizedStatus, item.item_type);
  return {
    key: normalizedStatus,
    label: formatSmartTaskStatusLabel(normalizedStatus, item.item_type) || 'Active',
    color: isTerminal ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800',
    borderColor: 'border-gray-300 dark:border-gray-600',
    dotColor: 'bg-gray-400',
    isTerminal,
    isOverSchedule: false,
    isOperationallyActive: false,
  };
}

function getRouteDisplayItemIds(item: RouteDisplayStatusLike): string[] {
  return [item.id, item.parentJobId, item.appointmentId].filter(Boolean) as string[];
}

export function isUpcomingRouteSnapshotStatus(status?: string | null): boolean {
  return getUpcomingRouteSnapshotStatusKeys().has(normalizeStatusKey(status));
}

export function isLiveWorkflowStatus(status?: string | null): boolean {
  const normalized = normalizeStatusKey(status);
  return getTravelStatusKeys().has(normalized)
    || getActiveDisplayStatusKeys().has(normalized)
    || normalized === 'paused'
    || isSmartTaskTerminalStatus(normalized);
}

export function resolveRouteDisplayStatus({
  routeStatus,
  appointmentStatus,
  parentJobStatus,
}: {
  routeStatus?: string | null;
  appointmentStatus?: string | null;
  parentJobStatus?: string | null;
}): string {
  if (isUpcomingRouteSnapshotStatus(routeStatus)) {
    if (isLiveWorkflowStatus(appointmentStatus)) {
      return appointmentStatus as string;
    }

    if (!appointmentStatus && isLiveWorkflowStatus(parentJobStatus)) {
      return parentJobStatus as string;
    }
  }

  return routeStatus || appointmentStatus || parentJobStatus || 'pending';
}

export function isRouteDisplayItemOverSchedule(
  item: RouteDisplayStatusLike,
  context: WorkItemStatusContext = {},
): boolean {
  const appointmentRef = (item as RouteDisplayStatusLike & {
    _sourceAppointmentItem?: WorkItem;
    _sourceWorkItem?: WorkItem;
  })._sourceAppointmentItem;
  if (appointmentRef && normalizeStatusKey(appointmentRef.item_type) === 'appointment') {
    return isWorkItemOverSchedule(appointmentRef, context);
  }

  const source = (item as RouteDisplayStatusLike & { _sourceWorkItem?: WorkItem })._sourceWorkItem;
  if (source && normalizeStatusKey(source.item_type) === 'appointment') {
    return isWorkItemOverSchedule(source, context);
  }

  const workflowContext = resolveContext(context);
  const normalizedState = normalizeStatusKey(workflowContext.current_state);
  const idsToCheck = getRouteDisplayItemIds(item);
  const activeWorkItemId = workflowContext.active_work_item_id != null
    ? String(workflowContext.active_work_item_id)
    : null;
  const activeWorkflowMatch = activeWorkItemId != null && idsToCheck.some((id) => String(id) === activeWorkItemId);
  const normalizedStatus = normalizeStatusKey(item.status);

  const isActivelyWorked = (activeWorkflowMatch && ['checked_in', 'working', 'in_progress', 'paused', 'completing'].includes(normalizedState))
    || getActiveWorkStatusKeys().has(normalizedStatus);

  if (!isActivelyWorked) {
    return false;
  }

  const startMs = parseTimestamp(item.scheduled_start);
  const endMs = parseTimestamp(item.scheduled_end);
  if (startMs == null || endMs == null || endMs <= startMs) {
    return false;
  }

  const allocatedDurationMs = endMs - startMs;
  const startedAtMs = parseTimestamp(item.started_at);
  if (startedAtMs != null) {
    return Date.now() - startedAtMs > allocatedDurationMs;
  }

  return Date.now() > endMs;
}

export function getRouteDisplayStopState(
  item: RouteDisplayStatusLike,
  context: WorkItemStatusContext = {},
): RouteDisplayStopState {
  const workflowContext = resolveContext(context);
  const normalizedState = normalizeStatusKey(workflowContext.current_state);
  const activeWorkItemId = workflowContext.active_work_item_id != null
    ? String(workflowContext.active_work_item_id)
    : null;
  const travelingToWorkItemId = workflowContext.traveling_to_work_item_id != null
    ? String(workflowContext.traveling_to_work_item_id)
    : null;
  const idsToCheck = getRouteDisplayItemIds(item);
  const normalizedStatus = normalizeStatusKey(item.status);

  if (isSmartTaskTerminalStatus(normalizedStatus)) {
    return 'completed';
  }

  const travelWorkflowMatch = travelingToWorkItemId != null
    && idsToCheck.some((id) => String(id) === travelingToWorkItemId)
    && ['traveling', 'travel_paused'].includes(normalizedState);
  if (travelWorkflowMatch || getTravelStatusKeys().has(normalizedStatus)) {
    return 'traveling';
  }

  const activeWorkflowMatch = activeWorkItemId != null && idsToCheck.some((id) => String(id) === activeWorkItemId);
  if (isRouteDisplayItemOverSchedule(item, context)) {
    return 'over_schedule';
  }

  if (
    (activeWorkflowMatch && ['arrived', 'checked_in', 'paused', 'completing'].includes(normalizedState))
    || ['arrived', 'current', 'on_site'].includes(normalizedStatus)
    || getActiveWorkStatusKeys().has(normalizedStatus)
  ) {
    return 'active';
  }

  return 'upcoming';
}